import React from 'react';
import { motion } from 'framer-motion';
import { spas } from '../data/charlotteSpas';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTag, FiClock, FiArrowRight, FiScissors, FiShield } = FiIcons;

const Deals = () => {
  return (
    <div className="min-h-screen bg-[#fafaf9] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-950 text-white px-6 py-2.5 rounded-full text-sm font-bold mb-6 shadow-xl tracking-widest uppercase">
            <SafeIcon icon={FiTag} className="w-4 h-4" />
            Voucher Marketplace
          </div>
          <h1 className="font-playfair text-5xl md:text-7xl font-bold text-stone-900 mb-6">Massage Deals</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-xl font-light">Direct-from-owner promotions. No booking fees, no middleman—just pure relaxation.</p>
        </header>

        <div className="grid grid-cols-1 gap-12">
          {spas.map((spa) => (
            <motion.div 
              key={spa.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative overflow-visible"
            >
              {/* Voucher Aesthetic Container */}
              <div className="bg-white rounded-[2.5rem] shadow-xl border border-stone-200 flex flex-col lg:flex-row min-h-[320px]">
                
                {/* Left Side: Business Info */}
                <div className="w-full lg:w-1/3 p-8 border-b lg:border-b-0 lg:border-r border-dashed border-stone-300 relative">
                  {/* Coupon Cutout circles */}
                  <div className="hidden lg:block absolute -top-4 -right-4 w-8 h-8 bg-[#fafaf9] rounded-full shadow-inner border border-stone-200"></div>
                  <div className="hidden lg:block absolute -bottom-4 -right-4 w-8 h-8 bg-[#fafaf9] rounded-full shadow-inner border border-stone-200"></div>
                  
                  <div className="h-full flex flex-col">
                    <img src={spa.imageUrl} className="w-full h-40 object-cover rounded-2xl mb-6 shadow-md" alt="" />
                    <h4 className="text-xs font-black text-stone-400 uppercase tracking-widest mb-2">{spa.neighborhood}</h4>
                    <h3 className="font-playfair text-2xl font-bold text-stone-900 mb-1">{spa.name}</h3>
                    <p className="text-stone-500 text-sm">{spa.type}</p>
                  </div>
                </div>

                {/* Right Side: Deal Details */}
                <div className="flex-grow p-8 md:p-12 flex flex-col justify-center bg-indigo-50/30">
                  <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
                    <div>
                      <div className="flex items-center gap-2 text-indigo-700 mb-4">
                        <SafeIcon icon={FiScissors} className="w-5 h-5" />
                        <span className="font-black text-sm uppercase tracking-tighter">Exclusive Voucher Offer</span>
                      </div>
                      <h2 className="font-playfair text-4xl font-bold text-stone-900 mb-4">{spa.deal.title}</h2>
                      <p className="text-stone-600 text-lg max-w-xl leading-relaxed">{spa.deal.description}</p>
                    </div>
                    
                    <div className="bg-white border-2 border-indigo-900 rounded-3xl p-6 text-center shadow-lg min-w-[180px]">
                      <p className="text-[10px] text-stone-400 font-black uppercase tracking-widest mb-2">Discount Value</p>
                      <p className="text-3xl font-black text-indigo-900">{spa.deal.discountValue}</p>
                      <div className="mt-4 pt-4 border-t border-stone-100">
                         <p className="text-[10px] text-stone-400 font-black uppercase tracking-widest mb-1">Use Promo Code</p>
                         <code className="bg-indigo-50 text-indigo-900 px-3 py-1 rounded-lg font-mono font-bold text-lg">{spa.deal.code}</code>
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto pt-8 border-t border-stone-200 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex flex-col gap-2">
                       <div className="flex items-center text-stone-500 text-sm font-medium">
                        <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-indigo-400" />
                        Expires {spa.deal.expires}
                      </div>
                      <div className="flex items-center text-stone-400 text-[10px] font-bold uppercase tracking-widest">
                        <SafeIcon icon={FiShield} className="w-3 h-3 mr-1" />
                        {spa.deal.terms}
                      </div>
                    </div>
                    <Link 
                      to={`/spa/${spa.id}`}
                      className="bg-indigo-900 text-white px-10 py-4 rounded-2xl font-bold flex items-center gap-3 hover:bg-indigo-950 transition-all shadow-xl hover:shadow-indigo-900/30"
                    >
                      Redeem Reward
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

              </div>
            </motion.div>
          ))}
        </div>

        {/* Incentive Section */}
        <div className="mt-24 bg-stone-900 rounded-[3.5rem] p-12 md:p-24 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full -mr-48 -mt-48 blur-[120px]"></div>
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-playfair text-4xl md:text-6xl font-bold mb-8 leading-tight">Get Charlotte's Best Deals First.</h2>
              <p className="text-stone-400 text-xl mb-12 leading-relaxed">Join our inner circle to receive "Last Minute Opening" notifications from top spas, often with discounts up to 50%.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="flex-grow bg-white/5 border border-white/10 rounded-2xl px-8 py-5 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white placeholder-stone-500 text-lg"
                />
                <button className="bg-indigo-600 text-white px-10 py-5 rounded-2xl font-bold hover:bg-indigo-500 transition-all shadow-2xl text-lg">
                  Subscribe
                </button>
              </div>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white/5 backdrop-blur-md p-8 rounded-[2rem] border border-white/10">
                  <div className="w-10 h-10 bg-indigo-500 rounded-xl mb-4 opacity-50"></div>
                  <div className="h-4 w-3/4 bg-white/20 rounded mb-2"></div>
                  <div className="h-3 w-1/2 bg-white/10 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deals;